import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { usePagination } from "@/hooks/usePagination";
import { Pagination } from "@/components/ui/pagination";
import { blogListParams, fetchAllAuthorPosts } from "@/components/blog/blog-list";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Plus, CircleNotch, Newspaper } from "@phosphor-icons/react";
import { BlogSummary } from "@/components/blog/BlogSummary";
import { TherapistPostCard, TherapistBlogEmptyState } from "@/components/blog/TherapistPostCard";
import { throwIfNotOk } from "@/lib/api-error";
import type { TherapistBlogPost } from "@/types";

export const Route = createFileRoute("/dashboard/therapist/blog/")({
  component: TherapistBlog,
});

type BlogPost = TherapistBlogPost;
type BlogListResponse = { data: BlogPost[]; total: number; page: number };

const POSTS_PER_PAGE = 10;

async function fetchBlogPage(params: URLSearchParams): Promise<BlogListResponse> {
  const res = await fetch(`/api/blog?${params}`);
  if (!res.ok) throw new Error("Greška pri učitavanju članaka");
  return res.json() as Promise<BlogListResponse>;
}

function TherapistBlog() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const authorId = user?.id;
  const [deletingPostId, setDeletingPostId] = useState<string | null>(null);
  const { page, setPage, pageSize } = usePagination({ pageSize: POSTS_PER_PAGE });

  const { data, isLoading } = useQuery({
    queryKey: ["therapist", "blog", authorId, "page", page],
    queryFn: () => fetchBlogPage(blogListParams({ page, limit: pageSize, authorId })),
    enabled: !!authorId,
    placeholderData: (prev) => prev,
  });

  // Sažetak po statusu treba sve članke, ne samo trenutnu stranicu.
  const { data: allPosts, isLoading: summaryLoading } = useQuery({
    queryKey: ["therapist", "blog", authorId, "all"],
    queryFn: () => fetchAllAuthorPosts(authorId!, fetchBlogPage),
    enabled: !!authorId,
  });

  const posts = data?.data ?? [];
  const totalPages = Math.ceil((data?.total ?? 0) / pageSize);
  // Brisanje zadnjeg članka na zadnjoj stranici: vrati se na postojeću stranicu.
  if (data && page > 1 && page > totalPages) setPage(Math.max(1, totalPages));

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/blog/${id}/update`, { method: "DELETE" });
      await throwIfNotOk(res, "Greška pri brisanju članka");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["therapist", "blog"] });
      queryClient.invalidateQueries({ queryKey: ["blog"] });
      setDeletingPostId(null);
    },
  });

  return (
    <div className="relative mx-auto max-w-6xl space-y-5 pb-14 sm:space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <h1
            className="text-3xl font-semibold leading-tight tracking-tight text-foreground sm:text-4xl"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            Moji članci
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Pišite, uređujte i objavljujte svoja stručna razmišljanja na platformi.
          </p>
        </div>

        <Link
          to="/dashboard/therapist/blog/new"
          className="group inline-flex shrink-0 items-center gap-2 self-start rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground shadow-sm shadow-primary/20 transition-all hover:shadow-md hover:shadow-primary/30 hover:brightness-110 active:scale-[0.98] sm:self-auto"
        >
          <Plus className="size-4 transition-transform group-hover:rotate-90" weight="bold" />
          Novi članak
        </Link>
      </div>

      <BlogSummary posts={allPosts ?? []} isLoading={summaryLoading} />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="flex min-w-0 items-center gap-3">
          <Newspaper className="size-5 shrink-0 text-primary" />
          <h2 className="text-lg font-semibold text-foreground" style={{ fontFamily: "var(--font-heading)" }}>
            Svi članci
          </h2>
          {isLoading && <div className="size-4 animate-spin rounded-full border-2 border-primary/20 border-t-primary" />}
        </div>
      </div>

      <div className="rounded-2xl border border-border/60 bg-card px-5 py-3 shadow-sm sm:px-6 sm:py-4">
        {isLoading && posts.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <CircleNotch className="size-6 animate-spin text-muted-foreground" />
          </div>
        ) : posts.length === 0 ? (
          <TherapistBlogEmptyState />
        ) : (
          posts.map((post) => (
            <TherapistPostCard
              key={post.id}
              post={post}
              onDelete={() => setDeletingPostId(post.id)}
              deleting={deleteMutation.isPending}
            />
          ))
        )}
      </div>

      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />

      <ConfirmDialog
        open={deletingPostId !== null}
        title="Obriši članak"
        description="Da li ste sigurni da želite obrisati ovaj članak? Ovu radnju nije moguće poništiti."
        confirmLabel="Obriši"
        tone="destructive"
        isPending={deleteMutation.isPending}
        onClose={() => setDeletingPostId(null)}
        onConfirm={() => deletingPostId && deleteMutation.mutate(deletingPostId)}
      />
    </div>
  );
}
